// Bomb the Hill's endless road (#195; split out of `hill-course.ts` by
// #208): the stretch of the course the park covers now.

import { CHUNK, COURSE, type CourseChunk, type CourseTuning, type HillCourse, type HillObstacle, type HillTraffic } from "./hill-course";
import { CourseGen, nextSeed } from "./hill-gen";
import { TrafficFlow } from "./hill-traffic";

/**
 * Bomb the Hill's endless road (#195). The park is a fixed stretch of
 * street, and the course runs on past its end forever: every time the rider
 * gets `CHUNK` metres further down, the road (and the rider with it) moves
 * back up by `CHUNK`, and the course is laid out further ahead. `course` is
 * what's in the road where the park is now, in its own z, which is what the
 * sim hits and the renderer draws; `offset` is how far the road has moved.
 * Each run gets its own seed (`reset`), kept so a run can be replayed.
 */
export class HillRoad {
  /** The course where the park is now: what the sim asks about. */
  course: HillCourse;
  /** How far the road has moved back up (m): course z = park z + offset. */
  offset = 0;
  /** This run's chunks, in order. */
  chunks: CourseChunk[] = [];
  /** The seed the next run gets, from this one: Game swaps in a random one so no two runs are alike. */
  pickSeed: (last: number) => number = nextSeed;
  /** How the next run's road is laid out (`reset`): the tutorial's road while it runs (#201). */
  tuning: CourseTuning = COURSE;
  private gen: CourseGen;
  /**
   * The traffic lane's vehicles (#212). Each sets off in a chunk but drives
   * on down the hill into later ones, so it's kept here, not with its chunk:
   * a slow rider's traffic gets ahead, and dropping it with its chunk made it
   * vanish in front of them. Dropped once it's well behind the park's top.
   */
  private cars: HillTraffic[] = [];
  /** How the traffic lane drives this run, stopping at the side streets' lights (#214). */
  private flow = new TrafficFlow();
  /** The chunk after the last one laid out (#214): laid out already so the traffic lane knows its lights. */
  private ahead: CourseChunk | null = null;

  /**
   * A road `halfWidth` either side of x = 0, from `top` (the spawn's z) to
   * `bottom` (the park's z where the road ends), first laid out from `seed`.
   */
  constructor(
    seed: number,
    readonly halfWidth: number,
    readonly top: number,
    readonly bottom: number,
  ) {
    this.gen = new CourseGen(seed, halfWidth, top, this.tuning);
    this.course = this.window(0);
    this.layOut();
  }

  /** This run's seed. */
  get seed(): number {
    return this.gen.seed;
  }

  /** A fresh run from the top on `seed`, its traffic set going at sim time `clock`. */
  reset(seed: number, clock = 0): void {
    this.gen = new CourseGen(seed, this.halfWidth, this.top, this.tuning);
    this.offset = 0;
    this.chunks = [];
    this.cars = [];
    this.flow = new TrafficFlow();
    this.ahead = null;
    this.layOut(clock);
  }

  /** The next run: a new seed from `pickSeed`. */
  nextRun(clock = 0): void {
    this.reset(this.pickSeed(this.seed) >>> 0, clock);
  }

  /**
   * Moves the road back up by `CHUNK` (the rider's been moved up with it) at
   * sim time `clock`, laying out what's now in reach and dropping the
   * traffic that's by then well behind.
   */
  advance(clock = this.course.t0): void {
    this.offset += CHUNK;
    this.layOut(this.course.t0, clock);
  }

  /** Lays out the chunks that reach the park now, drops the ones behind it (and the traffic behind it at `now`), and builds `course`. */
  private layOut(t0 = this.course?.t0 ?? 0, now = t0): void {
    const from = this.top - CHUNK + this.offset;
    const to = this.bottom + CHUNK + this.offset;
    while (this.gen.z0 + (this.chunks.at(-1)?.k ?? -1) * CHUNK + CHUNK < to) {
      // The traffic lane sees a side street's lights a chunk before anything else does (#214), so nothing's caught in one as it's laid out.
      const chunk = this.ahead ?? this.lookAhead();
      this.ahead = this.lookAhead();
      this.chunks.push(chunk);
      this.cars.push(...chunk.traffic);
      for (const v of chunk.traffic) this.flow.add(v, now - t0);
    }
    // Keep one chunk behind the top.
    this.chunks = this.chunks.filter((c) => this.top + (c.k + 1) * CHUNK > from);
    // Traffic only drives on down the hill, so once it's well behind it stays behind (in course z, like `from`).
    this.cars = this.cars.filter((v) => {
      if (this.flow.z(v, now - t0) + v.hl > from) return true;
      this.flow.drop(v);
      return false;
    });
    this.course = this.window(t0);
  }

  /** The chunk laid out after the last of `chunks`, ahead of the park (#202: a crosswalk's shopfront there can reach back into the last one's roadside). */
  get next(): CourseChunk | null {
    return this.ahead;
  }

  /** Lays out the next chunk, its side streets' lights in the traffic lane's sight already (#214). */
  private lookAhead(): CourseChunk {
    const chunk = this.gen.next();
    this.flow.addJunctions(chunk.junctions);
    return chunk;
  }

  private window(t0: number): HillCourse {
    const dz = this.offset;
    const shift = <T extends { z: number }>(list: T[]): T[] => list.map((o) => ({ ...o, z: o.z - dz }));
    const all = (pick: (c: CourseChunk) => HillObstacle[]) => shift(this.chunks.flatMap(pick)).sort((p, q) => p.z - q.z);
    return {
      z0: this.top,
      z1: this.bottom,
      halfWidth: this.halfWidth,
      lane: this.gen.lane,
      obstacles: all((c) => c.obstacles),
      traffic: [...this.cars],
      ramps: shift(this.chunks.flatMap((c) => c.ramps)),
      grinds: shift(this.chunks.flatMap((c) => c.grinds)),
      gaps: shift(this.chunks.flatMap((c) => c.gaps)),
      crossings: shift(this.chunks.flatMap((c) => c.crossings)),
      walkers: shift(this.chunks.flatMap((c) => c.walkers)).sort((p, q) => p.z - q.z),
      junctions: shift(this.chunks.flatMap((c) => c.junctions)),
      crossers: shift(this.chunks.flatMap((c) => c.crossers)).sort((p, q) => p.z - q.z),
      offset: dz,
      t0,
    };
  }
}
